/**
 * Phase 3 — OptimisationProgress
 *
 * Live progress banner: stage strip + per-candidate progress bar +
 * current best Pk + cancel button. Polled by the parent — this
 * component is render-only.
 */
import { Hourglass, Hammer, Search, Cpu, BarChart3, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const STAGES = [
    { id: "pending", label: "Pending", Icon: Hourglass },
    { id: "baseline", label: "Baseline", Icon: Hammer },
    { id: "sensitivity", label: "Sensitivity", Icon: BarChart3 },
    { id: "sampling", label: "Sampling", Icon: Search },
    { id: "evaluating", label: "Evaluating", Icon: Cpu },
    { id: "reporting", label: "Reporting", Icon: BarChart3 },
];

function stageIndex(stage) {
    const i = STAGES.findIndex((s) => s.id === stage);
    return i < 0 ? 0 : i;
}

export default function OptimisationProgress({ study, onCancel, cancelling }) {
    const progress = study?.progress || {};
    const stage = progress.stage || "pending";
    const completed = progress.candidates_completed || 0;
    const total = progress.candidates_total || 0;
    const cached = progress.candidates_cached || 0;
    const failed = progress.candidates_failed || 0;
    const pct = total > 0 ? Math.min(100, (completed / total) * 100) : 0;
    const bestPk = progress.current_best_pk;
    const stageNow = stageIndex(stage);

    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 space-y-4"
            data-testid="optimisation-progress"
        >
            {/* Stage strip */}
            <ol
                className="flex items-stretch overflow-x-auto"
                data-testid="optimisation-stage-strip"
            >
                {STAGES.map((s, idx) => {
                    const Icon = s.Icon;
                    const past = idx < stageNow;
                    const cur = idx === stageNow;
                    return (
                        <li
                            key={s.id}
                            className={[
                                "flex-1 min-w-[120px] flex items-center gap-2 px-3 py-2 border-r border-ux-border-1 last:border-r-0",
                                cur ? "bg-ux-accent-soft/40" : past ? "bg-ux-bg-2" : "bg-transparent",
                            ].join(" ")}
                            data-testid={`stage-cell-${s.id}`}
                            data-current={cur ? "true" : "false"}
                        >
                            <Icon
                                className={[
                                    "w-3.5 h-3.5",
                                    cur
                                        ? "text-ux-accent"
                                        : past
                                          ? "text-ux-success"
                                          : "text-ux-text-3",
                                ].join(" ")}
                            />
                            <span
                                className={[
                                    "text-[11px] uppercase tracking-wider",
                                    cur
                                        ? "text-ux-accent"
                                        : past
                                          ? "text-ux-text-2"
                                          : "text-ux-text-3",
                                ].join(" ")}
                            >
                                {s.label}
                            </span>
                            {cur && (
                                <span
                                    className="ml-auto pulse-dot"
                                    aria-hidden
                                    data-testid={`stage-current-dot-${s.id}`}
                                />
                            )}
                        </li>
                    );
                })}
            </ol>

            {/* Candidate progress + best Pk */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2 space-y-2">
                    <div className="flex items-baseline gap-2">
                        <span className="text-[12px] uppercase tracking-wider text-ux-text-3">
                            Candidates
                        </span>
                        <span
                            className="text-[12px] font-mono tabular-nums text-ux-text-1"
                            data-testid="optimisation-candidates-readout"
                        >
                            {completed}
                            {total ? ` / ${total}` : ""}
                        </span>
                        {cached > 0 && (
                            <span className="text-[10px] uppercase tracking-wider text-ux-info">
                                {cached} cache hits
                            </span>
                        )}
                        {failed > 0 && (
                            <span className="text-[10px] uppercase tracking-wider text-ux-warning">
                                {failed} failed
                            </span>
                        )}
                    </div>
                    <Progress
                        value={pct}
                        className="h-1.5"
                        data-testid="optimisation-progress-bar"
                    />
                    {progress.message && (
                        <p
                            className="text-[11px] text-ux-text-3 italic"
                            data-testid="optimisation-progress-msg"
                        >
                            {progress.message}
                        </p>
                    )}
                </div>
                <div className="space-y-1">
                    <span className="text-[10px] uppercase tracking-wider text-ux-text-3">
                        Current best Pk
                    </span>
                    <div
                        className="text-[24px] font-medium tabular-nums text-ux-text-1"
                        data-testid="optimisation-best-pk"
                    >
                        {typeof bestPk === "number" ? bestPk.toFixed(3) : "—"}
                    </div>
                </div>
            </div>

            {/* Cancel */}
            {(study?.status === "queued" || study?.status === "running") && (
                <div className="flex justify-end pt-1 border-t border-ux-border-1">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onCancel}
                        disabled={!!cancelling}
                        data-testid="optimisation-cancel-button"
                    >
                        <Square className="w-3 h-3 mr-1" />
                        {cancelling ? "Cancelling…" : "Cancel study"}
                    </Button>
                </div>
            )}
        </div>
    );
}
